import { CopyIcon, TrashIcon } from 'assets/icons';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import Button from 'components/button';
import { cardIconButtonClass } from 'components/card/PanelCard';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import StyledDateInput from 'components/styledInput/StyledDateInput';
import StyledInput from 'components/styledInput/StyledInput';
import FieldLabel from 'components/styledInput/FieldLabel';
import { companyRoleLabel } from 'constants/companyRoles';
import { fr } from 'date-fns/locale';
import { crmTaskErrorText } from 'helper/crmTaskErrors';
import { addDays, dayLabel, fromIsoDay, isoDay, parseClock } from 'helper/timeLabels';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useTaskPanels from 'hooks/useTaskPanels';
import { useNotifyTasksChanged } from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import ChoiceChips from '../ChoiceChips';

registerLocale('fr-FR', fr);

// A task has a single kind: a rendez-vous is created in the planning, a call in
// the call list. An old Appel, E-mail, Visite or Réunion task becomes a plain
// task when saved.
const TASK_TYPE = 'todo';
const TIMES = ['9:00', '11:00', '14:00', '16:00'];
const DURATIONS = [15, 30, 45, 60, 90, 120];
const DEFAULT_DURATION = 15;

const pad = (value) => String(value).padStart(2, '0');
const nextMonday = (now) => addDays(now, ((8 - now.getDay()) % 7) || 7);

// The option is kept by its key: two companies may share a name, so a label
// does not tell them apart.
const emptyForm = (defaults, contacts) => ({
  title: '',
  note: '',
  dueOn: defaults.dueOn ?? isoDay(new Date()),
  time: defaults.time ?? '',
  duration: defaults.duration ?? DEFAULT_DURATION,
  durationTouched: Boolean(defaults.duration),
  assigneeId: '',
  // A record page opens the form on its company or contact (its label).
  contact: defaults.contact ?? '',
  contactKey: (defaults.contact && contacts.find((option) => option.label === defaults.contact)?.key) || null,
  contactTouched: false,
});
// The task's record is its company, or its big group when it has no company.
const optionOf = (task, contacts) => {
  const sameRecord = (option) =>
    task.companyId ? option.companyId === task.companyId : Boolean(task.groupId) && option.groupId === task.groupId;
  return (
    (task.contactName ? contacts.find((option) => sameRecord(option) && option.contact === task.contactName) : null) ??
    contacts.find((option) => sameRecord(option) && !option.contact) ??
    null
  );
};
const formOf = (task, contacts) => ({
  title: task.title,
  note: task.note ?? '',
  dueOn: task.dueOn,
  time: task.dueTime ? task.dueTime.slice(0, 5).replace(/^0/, '') : '',
  duration: task.durationMin ?? DEFAULT_DURATION,
  durationTouched: true,
  assigneeId: task.assigneeId,
  contact: optionOf(task, contacts)?.label ?? '',
  contactKey: optionOf(task, contacts)?.key ?? null,
  contactTouched: false,
});

/**
 * « Nouvelle tâche » and « Modifier la tâche » (slide-over `task_form`, with
 * `{ taskId, defaults }` in `objID`): the title, written by the user (no AI),
 * an optional description, the day and the time with their shortcuts, the length, the
 * colleague, the company or contact. ⌘ or Ctrl + Entrée saves. Why the save
 * button is grey is written beside it.
 */
const TaskFormPanel = () => {
  const { objID } = UseContext();
  const { taskId = null, defaults = {} } = objID && typeof objID === 'object' ? objID : {};
  const { language, text } = useText('taskForm');
  const { text: tasksText } = useText('tasks');
  const { call, me } = useCrmContext();
  const { toggleSlideOver } = useSlideOver();
  const { openTask } = useTaskPanels();
  const notify = useNotifyTasksChanged();
  const [task, setTask] = useState(null);
  const [team, setTeam] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const body = useRef(null);
  const submitRef = useRef(null);
  const editing = Boolean(taskId);
  const fail = (error) => {
    const message = crmTaskErrorText(error, tasksText);
    if (message) toast.error(message);
  };

  useEffect(() => {
    if (!me) return;
    const { taskId: id = null, defaults: given = {} } = objID && typeof objID === 'object' ? objID : {};
    setForm(null);
    Promise.all([
      id ? call(crmApi.getTask, { id }) : Promise.resolve(null),
      call(crmApi.getTeam),
      call(crmApi.getContacts),
    ]).then(
      ([found, people, options]) => {
        setTask(found);
        setTeam(people);
        setContacts(options);
        setForm(found ? formOf(found, options) : { ...emptyForm(given, options), assigneeId: me.id });
      },
      fail,
    );
  }, [call, me, objID]);

  // A new task opens on its title, as « N » then typing expects.
  const shown = Boolean(form);
  useEffect(() => {
    if (shown && !editing) body.current?.querySelector('input')?.focus({ preventScroll: true });
  }, [shown, editing]);

  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        submitRef.current?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const close = () => toggleSlideOver();
  // « Annuler » goes back to the task it edits.
  const cancel = () => (editing ? openTask(taskId) : close());

  if (!form) {
    return (
      <>
        <SlideOverHeader hide={close} closeLabel={text('close')}>
          {text(editing ? 'editTitle' : 'newTitle')}
        </SlideOverHeader>
        <SlideOverBody>
          <div className="mx-auto mt-16 h-6 w-6 animate-spin rounded-full border-2 border-yellow-500 border-t-transparent" />
        </SlideOverBody>
      </>
    );
  }

  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const selected = (form.contactKey && contacts.find((option) => option.key === form.contactKey)) || null;
  // An untouched field whose record is not among the options (list not loaded,
  // record gone) keeps the task's own: a group is never dropped silently.
  const kept = editing && !selected && !form.contactTouched;
  const clock = form.time.trim() ? parseClock(form.time) : null;
  const timeOk = !form.time.trim() || Boolean(clock);
  let blocker = null;
  if (!form.title.trim()) blocker = text('blockTitle');
  else if (!timeOk) blocker = text('blockTime');
  const now = new Date();
  const monday = nextMonday(now);

  const fields = () => ({
    type: TASK_TYPE,
    title: form.title.trim(),
    note: form.note.trim(),
    dueOn: form.dueOn,
    dueTime: clock ? `${pad(clock[0])}:${pad(clock[1])}` : null,
    durationMin: Number(form.duration),
    assigneeId: form.assigneeId,
    ...(kept
      ? {
          companyId: task.companyId ?? null,
          groupId: task.groupId ?? null,
          contactName: task.contactName ?? '',
          contactRole: task.contactRole ?? '',
          contactDetail: task.contactDetail ?? '',
        }
      : {
          companyId: selected?.companyId ?? null,
          groupId: selected?.groupId ?? null,
          contactName: selected?.contact ?? '',
          contactRole:
            typeof selected?.companyRole === 'number'
              ? companyRoleLabel(language, selected.companyRole)
              : (selected?.role ?? ''),
          contactDetail: selected?.phone ?? '',
        }),
  });

  // Out of today, the toast says when; given to a colleague, to whom.
  const savedMessage = () => {
    const when =
      form.dueOn === isoDay(now)
        ? ''
        : form.dueOn === isoDay(addDays(now, 1))
          ? text('whenTomorrow')
          : text('whenDay', { day: dayLabel(fromIsoDay(form.dueOn)).toLowerCase() });
    const colleague = form.assigneeId !== me.id && form.assigneeId !== (task?.assigneeId ?? me.id);
    if (colleague) {
      const person = team.find((member) => member.id === form.assigneeId);
      return `${text('handed', { name: `${person.firstname} ${person.lastname}`.trim() })}${when}`;
    }
    return editing ? text('saved') : `${text('addedTodo')}${when}`;
  };

  const submit = async () => {
    if (blocker || busy) return;
    setBusy(true);
    try {
      const saved = editing
        ? await call(crmApi.updateTask, { id: taskId, fields: fields() })
        : await call(crmApi.createTask, { fields: fields() });
      // Added from the « Terminées » column of the kanban: done at once.
      if (!editing && defaults.done) await call(crmApi.setTaskDone, { id: saved.id, done: true });
      toast.success(savedMessage());
      notify();
      close();
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };
  submitRef.current = submit;

  const remove = async () => {
    try {
      await call(crmApi.deleteTask, { id: taskId });
      toast.success(text('deleted'));
      notify();
      close();
    } catch (error) {
      fail(error);
    }
  };
  const duplicate = async () => {
    try {
      await call(crmApi.createTask, { fields: { ...fields(), title: text('copyOf', { title: form.title }) } });
      toast.success(text('duplicated'));
      notify();
      close();
    } catch (error) {
      fail(error);
    }
  };

  return (
    <>
      <SlideOverHeader hide={close} closeLabel={text('close')}>
        <span className="block">{text(editing ? 'editTitle' : 'newTitle')}</span>
        <span className="mt-1 block text-sm font-normal text-gray-500">
          {editing
            ? text('createdBy', {
                name: task.createdByPerson ? `${task.createdByPerson.firstname} ${task.createdByPerson.lastname}`.trim() : '',
                day: dayLabel(new Date(task.createdAt)).toLowerCase(),
              })
            : text('newSubtitle')}
        </span>
      </SlideOverHeader>
      <SlideOverBody>
        <div ref={body} className="flex flex-col gap-5">
          <StyledInput
            type="text"
            label={text('titleLabel')}
            value={form.title}
            maxLength={120}
            onChange={(event) => set('title', event.target.value)}
          />
          <StyledInput
            type="textarea"
            label={text('noteLabel')}
            value={form.note}
            maxLength={2000}
            onChange={(event) => set('note', event.target.value)}
          />
          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
            <DatePicker
              dateFormat="dd/MM/yyyy"
              selected={fromIsoDay(form.dueOn)}
              onChange={(date) => date && set('dueOn', isoDay(date))}
              locale="fr-FR"
              popperPlacement="bottom"
              customInput={<StyledDateInput label={text('dayLabel')} />}
            />
            <StyledInput
              type="text"
              label={text('timeLabel')}
              placeholder="9:30"
              value={form.time}
              error={timeOk ? null : text('timeError')}
              onChange={(event) => set('time', event.target.value)}
            />
            <ChoiceChips
              options={[
                [isoDay(now), text('today')],
                [isoDay(addDays(now, 1)), text('tomorrow')],
                [isoDay(monday), text('monday', { day: monday.getDate() })],
              ]}
              value={form.dueOn}
              onPick={(day) => set('dueOn', day)}
            />
            <ChoiceChips
              options={TIMES.map((time) => [time, time])}
              value={form.time}
              onPick={(time) => set('time', form.time === time ? '' : time)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <FieldLabel label={text('durationLabel')} />
            <ChoiceChips
              options={DURATIONS.map((minutes) => [minutes, text(`duration_${minutes}`)])}
              value={Number(form.duration)}
              onPick={(minutes) => setForm((current) => ({ ...current, duration: minutes, durationTouched: true }))}
            />
          </div>
          <StyledInput
            type="select"
            label={text('assigneeLabel')}
            value={form.assigneeId}
            imgFront={<EmployeeAvatar employee={team.find((member) => member.id === form.assigneeId)} size={18} />}
            list={team.map((member) => ({
              value: member.id,
              label:
                member.id === me.id
                  ? text('me', { name: `${member.firstname} ${member.lastname}`.trim() })
                  : `${member.firstname} ${member.lastname}`.trim(),
            }))}
            onChange={(event) => set('assigneeId', event.target.value)}
          />
          <StyledInput
            type="autocomplete"
            label={text('contactLabelOptional')}
            placeholder={text('contactPlaceholder')}
            value={form.contact}
            list={contacts.map((option) => ({ value: option.key, label: option.label }))}
            hint={text('contactHint')}
            onChange={(event) =>
              setForm((current) => ({ ...current, contact: event.target.value, contactKey: null, contactTouched: true }))
            }
            onPick={(option) => setForm((current) => ({ ...current, contact: option.label, contactKey: option.value }))}
          />
        </div>
      </SlideOverBody>
      <div className="flex-shrink-0 border-t border-gray-100">
        <SlideOverFooter>
          {editing && (
            <div className="flex items-center gap-2">
              <IconTooltip label={text('delete')}>
                <button type="button" aria-label={text('delete')} onClick={remove} className={cardIconButtonClass}>
                  <TrashIcon wh={18} color="#DC2626" />
                </button>
              </IconTooltip>
              <IconTooltip label={text('duplicate')}>
                <button type="button" aria-label={text('duplicate')} onClick={duplicate} className={cardIconButtonClass}>
                  <CopyIcon wh={18} />
                </button>
              </IconTooltip>
            </div>
          )}
          <span className="mr-auto text-xs text-gray-500">{blocker}</span>
          <Button color="SECONDARY" size="SM" onClick={cancel}>
            {text('cancel')}
          </Button>
          <Button color={blocker || busy ? 'DISABLED' : 'PRIMARY'} size="SM" disabled={Boolean(blocker) || busy} onClick={submit}>
            {busy ? text('busy') : text(editing ? 'save' : 'ctaTodo')}
          </Button>
        </SlideOverFooter>
      </div>
    </>
  );
};

export default TaskFormPanel;
